import {
    RuntimeFormError,
    type FormErrorCode,
    type UploadGrant,
} from './types';

const PURPOSE = 'FORM_ATTACHMENT';

const GRANT_STATUS_CODES: Record<number, FormErrorCode> = {
    // The grant validates size then file type, and refuses either as InvalidInput. The only upload
    // failure a visitor can act on, so it is the only one worth its own code.
    400: 'FILE_REJECTED',
    429: 'RATE_LIMITED',
};

/**
 * The monolith's own runtime endpoint, reached by an absolute path so the preview's <base href>
 * cannot rewrite it. Editor servers serve it too, with the domain check no-oped, so a preview
 * uploads through the same call a published page makes.
 */
function grantUrl(siteAlias: string): string {
    return `/_dm/s/rt/api/public/rt/site/${encodeURIComponent(siteAlias)}/uploads/grant`;
}

async function messageFrom(
    response: Response,
    fallback: string,
): Promise<string> {
    try {
        const body = (await response.json()) as { message?: string };
        if (body?.message) {
            return body.message;
        }
    } catch {
        // S3's XML, a gateway error page, an empty 429.
    }
    return `${fallback} (status ${response.status})`;
}

async function requestGrants(
    siteAlias: string,
    files: File[],
): Promise<UploadGrant[]> {
    let response: Response;
    try {
        response = await fetch(grantUrl(siteAlias), {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                purpose: PURPOSE,
                files: files.map((file) => ({
                    fileName: file.name,
                    contentType: file.type,
                    size: file.size,
                })),
            }),
        });
    } catch {
        throw new RuntimeFormError(
            0,
            'NETWORK',
            'the upload could not reach the server',
        );
    }

    if (!response.ok) {
        throw new RuntimeFormError(
            response.status,
            GRANT_STATUS_CODES[response.status] ?? 'UNKNOWN',
            await messageFrom(response, 'the upload was not granted'),
        );
    }

    // Grants come back positionally. A short list would pair files with the wrong key and drop the
    // tail without anyone noticing, so it fails the whole selection instead.
    const granted = (await response.json()) as UploadGrant[];
    if (!Array.isArray(granted) || granted.length !== files.length) {
        throw new RuntimeFormError(
            response.status,
            'UNKNOWN',
            `the server granted ${granted?.length ?? 0} of ${files.length} uploads`,
        );
    }
    return granted;
}

function bodyFor(grant: UploadGrant, file: File): FormData {
    const body = new FormData();
    Object.entries(grant.fields).forEach(([name, value]) =>
        body.append(name, value),
    );
    body.append('file', file);
    return body;
}

async function store(grant: UploadGrant, file: File): Promise<string> {
    let response: Response;
    try {
        response = await fetch(grant.uploadUrl, {
            method: 'POST',
            body: bodyFor(grant, file),
        });
    } catch {
        throw new RuntimeFormError(
            0,
            'NETWORK',
            `${file.name} could not be uploaded`,
        );
    }

    if (!response.ok) {
        throw new RuntimeFormError(
            response.status,
            'UNKNOWN',
            await messageFrom(response, `${file.name} was refused by storage`),
        );
    }

    return grant.key;
}

/**
 * Every file of one submission in a single grant call: the endpoint spends a rate-limiter token per
 * request, not per file. Returns the stored keys in the order the files were given.
 */
export async function uploadFiles(
    siteAlias: string,
    files: File[],
): Promise<string[]> {
    const grants = await requestGrants(siteAlias, files);

    // Settled, not fail-fast: rejecting on the first failure would return while the other uploads
    // are still in flight, leaving objects in staging that no submission claims.
    const stored = await Promise.allSettled(
        grants.map((grant, index) => store(grant, files[index])),
    );
    const failure = stored.find((result) => result.status === 'rejected');
    if (failure) {
        throw failure.reason;
    }
    return stored.map(
        (result) => (result as PromiseFulfilledResult<string>).value,
    );
}
